import React from 'react';
import { SelectProps } from '@duxton-web/ui';

import { AppVersion, CountrySelector } from '@/common/components';
import FuseSelectComponent from '@/common/selects/FuseSelect/FuseSelect';
import LanguageInput from '@/common/selects/LanguageInput';
import Service from '@/lib/App';
import { Constraint } from '@/lib/PlannerDTOs';
import { sortTreeData } from '@/pages/RolloutTab/RolloutEditor/RolloutBlock/ConstraintBlocks/ConstraintBlock/ConstraintValueInput/ConstraintValueInput';
import SelectTimeInterval from '@/pages/RolloutTab/RolloutEditor/RolloutBlock/ConstraintBlocks/ConstraintBlock/ConstraintValueInput/SelectTimeInterval/SelectTimeInterval';
import { SelectTreeProps } from '@/pages/RolloutTab/RolloutEditor/RolloutBlock/ConstraintBlocks/ConstraintBlock/ConstraintValueInput/SelectTree';
import SelectTreeV2 from '@/pages/RolloutTab/RolloutEditor/RolloutBlock/ConstraintBlocks/ConstraintBlock/ConstraintValueInput/SelectTree/SelectTreeV2';
import TypeInput from '@/pages/RolloutTab/RolloutEditor/RolloutBlock/ConstraintBlocks/ConstraintBlock/ConstraintValueInput/TypeInput';
import {
  AutoSuggestDropDownList,
  DropDownList,
  MultiDropDownList,
  transformProps,
} from '@/pages/RolloutTab/RolloutEditor/RolloutBlock/ConstraintBlocks/ConstraintBlock/FacetConfigurationMap/FacetConfigurationMap';

export const ConstraintOperator = {
  IN: {
    label: 'in',
    value: 'in',
  },
  NOT_IN: {
    label: 'not in',
    value: 'not in',
  },
  IN_AREA: {
    label: 'in area',
    value: 'in area',
  },
  NOT_IN_AREA: {
    label: 'not in area',
    value: 'not in area',
  },
  CONTAINS: {
    label: 'contains',
    value: 'contains',
  },
  NOT_CONTAINS: {
    label: 'not contains',
    value: 'not contains',
  },
  EQUALS: {
    label: 'equals',
    value: '=',
  },
  NOT_EQUALS: {
    label: 'not equals',
    value: '<>',
  },
  GREATER_THAN_EQUALS: {
    label: 'greater than equal',
    value: '>=',
  },
  LESS_THAN_EQUALS: {
    label: 'less than equal',
    value: '<=',
  },
  GREATER_THAN: {
    label: 'greater than',
    value: '>',
  },
  LESS_THAN: {
    label: 'less than',
    value: '<',
  },
  SLOT: {
    label: 'slot',
    value: 'slot',
  },
};

interface InputProps {
  disabled?: boolean;
  value: string;
  constraint?: Constraint;
  availableValues?: { [key: string]: string };
  placeholder?: string;
  onChange: (e: any) => void;
}

// This static list needs to be updated. The source of truth comes from the backend
// https://gitlab.myteksi.net/gophers/go/-/blob/master/dispatcher/grab-x/commons/facet/facet.go
const facetMap = {
  '': {
    label: '',
    op: [
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: InputProps) => (
      <AutoSuggestDropDownList {...props} availableValues={{}} />
    ),
  },
  az: {
    label: 'Availability Zone',
    op: [
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: SelectTreeProps) => <TypeInput {...props} />,
  },
  city: {
    label: 'City',
    op: [
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: SelectTreeProps) => {
      const treeData = Service.cache!.getCityCountryTreeData(props?.multiple);
      const sortedTreeData = sortTreeData(treeData);

      return (
        <SelectTreeV2
          {...{
            ...props,
            treeData: sortedTreeData,
            treeNodeFilterProp: 'title',
            tokenizable: true,
          }}
        />
      );
    },
  },
  ctry: {
    label: 'Country',
    op: [
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: SelectTreeProps) => {
      return (
        <CountrySelector
          value={props.value}
          onChange={props.onChange}
          multiple={props.multiple}
        />
      );
    },
  },
  reg: {
    label: 'Region',
    op: [
      {
        label: ConstraintOperator.EQUALS.label,
        value: ConstraintOperator.EQUALS.value,
      },
      {
        label: ConstraintOperator.NOT_EQUALS.label,
        value: ConstraintOperator.NOT_EQUALS.value,
      },
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: InputProps) => {
      switch (props.constraint.op) {
        case ConstraintOperator.EQUALS.value:
        case ConstraintOperator.NOT_EQUALS.value:
          return <TypeInput {...props} />;
        default:
          return <AutoSuggestDropDownList {...props} />;
      }
    },
  },
  loc: {
    label: 'Location (lat, long)',
    op: [
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.IN_AREA.label,
        value: ConstraintOperator.IN_AREA.value,
        disabled: true,
      },
      {
        label: ConstraintOperator.NOT_IN_AREA.label,
        value: ConstraintOperator.NOT_IN_AREA.value,
        disabled: true,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: InputProps) => {
      switch (props.constraint.op) {
        case ConstraintOperator.IN_AREA.value:
          return <AutoSuggestDropDownList {...transformProps(props)} />;
        case ConstraintOperator.NOT_IN_AREA.value:
          return <AutoSuggestDropDownList {...transformProps(props)} />;
        default:
          return <AutoSuggestDropDownList {...props} />;
      }
    },
  },
  lgh: {
    label: 'Location Geohash',
    op: [
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: InputProps) => <AutoSuggestDropDownList {...props} />,
  },
  dolgh: {
    label: 'Dropoff Location Geohash',
    op: [
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: InputProps) => <AutoSuggestDropDownList {...props} />,
  },
  dvc: {
    label: 'App Version',
    op: [
      {
        label: ConstraintOperator.GREATER_THAN_EQUALS.label,
        value: ConstraintOperator.GREATER_THAN_EQUALS.value,
      },
      {
        label: ConstraintOperator.LESS_THAN_EQUALS.label,
        value: ConstraintOperator.LESS_THAN_EQUALS.value,
      },
      {
        label: ConstraintOperator.EQUALS.label,
        value: ConstraintOperator.EQUALS.value,
      },
    ],
    component: (props: SelectProps) => (
      <AppVersion
        disabled={props.disabled}
        value={props.value}
        onChange={props.onChange}
      />
    ),
  },
  svc: {
    label: 'Vehicle Type',
    op: [
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: InputProps) => {
      return <FuseSelectComponent {...props} />;
    },
  },
  dlc: {
    label: 'Device Locale',
    op: [
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: InputProps) => <MultiDropDownList {...props} />,
  },
  pax: {
    label: 'Passenger',
    op: [
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: InputProps) => <AutoSuggestDropDownList {...props} />,
  },
  dax: {
    label: 'Driver',
    op: [
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: InputProps) => <AutoSuggestDropDownList {...props} />,
  },
  area: {
    label: 'Area',
    op: [
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: InputProps) => <AutoSuggestDropDownList {...props} />,
  },
  mex: {
    label: 'Merchant',
    op: [
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: InputProps) => <AutoSuggestDropDownList {...props} />,
  },
  tex: {
    label: 'Taxi Vehicle',
    op: [
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: InputProps) => <AutoSuggestDropDownList {...props} />,
  },
  meu: {
    label: 'Mex User',
    op: [
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: InputProps) => <AutoSuggestDropDownList {...props} />,
  },
  mes: {
    label: 'Mex Store',
    op: [
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: InputProps) => <AutoSuggestDropDownList {...props} />,
  },
  bkg: {
    label: 'Booking Code',
    op: [
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: InputProps) => <AutoSuggestDropDownList {...props} />,
  },
  osv: {
    label: 'OS Version',
    op: [
      {
        label: ConstraintOperator.GREATER_THAN_EQUALS.label,
        value: ConstraintOperator.GREATER_THAN_EQUALS.value,
      },
      {
        label: ConstraintOperator.LESS_THAN_EQUALS.label,
        value: ConstraintOperator.LESS_THAN_EQUALS.value,
      },
      {
        label: ConstraintOperator.EQUALS.label,
        value: ConstraintOperator.EQUALS.value,
      },
      {
        label: ConstraintOperator.GREATER_THAN.label,
        value: ConstraintOperator.GREATER_THAN.value,
      },
      {
        label: ConstraintOperator.LESS_THAN.label,
        value: ConstraintOperator.LESS_THAN.value,
      },
    ],
    component: (props: InputProps) => <TypeInput {...props} />,
  },
  sid: {
    label: 'Session',
    op: [
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: InputProps) => <AutoSuggestDropDownList {...props} />,
  },
  req: {
    label: 'Request ID',
    op: [
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: InputProps) => <AutoSuggestDropDownList {...props} />,
  },
  seg: {
    label: 'Custom Segment',
    op: [
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: InputProps) => <AutoSuggestDropDownList {...props} />,
  },
  err: {
    label: 'Error',
    op: [
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: InputProps) => <AutoSuggestDropDownList {...props} />,
  },
  tag: {
    label: 'Tag',
    op: [
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: InputProps) => <AutoSuggestDropDownList {...props} />,
  },
  bch: {
    label: 'Batch ID',
    op: [
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: InputProps) => <AutoSuggestDropDownList {...props} />,
  },
  ser: {
    label: 'Series ID',
    op: [
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: InputProps) => <AutoSuggestDropDownList {...props} />,
  },
  host: {
    label: 'Host (iOS)',
    op: [
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: InputProps) => <AutoSuggestDropDownList {...props} />,
  },
  ip: {
    label: 'IP Address',
    op: [
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: InputProps) => <AutoSuggestDropDownList {...props} />,
  },
  tkn: {
    label: 'Auth Token',
    op: [
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: InputProps) => <AutoSuggestDropDownList {...props} />,
  },
  src: {
    label: 'Source',
    op: [
      {
        label: ConstraintOperator.EQUALS.label,
        value: ConstraintOperator.EQUALS.value,
      },
      {
        label: ConstraintOperator.NOT_EQUALS.label,
        value: ConstraintOperator.NOT_EQUALS.value,
      },
    ],
    component: (props: InputProps) => <AutoSuggestDropDownList {...props} />,
  },
  dla: {
    label: 'Device Language',
    op: [
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: InputProps) => <LanguageInput {...props} />,
  },
  lan: {
    label: 'Language Code (Backend)',
    op: [
      {
        label: ConstraintOperator.EQUALS.label,
        value: ConstraintOperator.EQUALS.value,
      },
      {
        label: ConstraintOperator.NOT_EQUALS.label,
        value: ConstraintOperator.NOT_EQUALS.value,
      },
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: InputProps) => {
      switch (props.constraint.op) {
        case ConstraintOperator.EQUALS.value:
        case ConstraintOperator.NOT_EQUALS.value:
          return <TypeInput {...props} />;
        default:
          return <AutoSuggestDropDownList {...props} />;
      }
    },
  },
  pre: {
    label: 'Backend Service',
    op: [
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: InputProps) => <AutoSuggestDropDownList {...props} />,
  },
  ts: {
    label: 'Time',
    op: [
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: InputProps) => <SelectTimeInterval {...props} />,
  },
  dvn: {
    label: 'Device Brand',
    op: [
      {
        label: ConstraintOperator.EQUALS.label,
        value: ConstraintOperator.EQUALS.value,
      },
      {
        label: ConstraintOperator.NOT_EQUALS.label,
        value: ConstraintOperator.NOT_EQUALS.value,
      },
      {
        label: ConstraintOperator.CONTAINS.label,
        value: ConstraintOperator.CONTAINS.value,
      },
      {
        label: ConstraintOperator.NOT_CONTAINS.label,
        value: ConstraintOperator.NOT_CONTAINS.value,
      },
    ],
    component: (props: InputProps) => (
      <TypeInput {...props} placeholder="Enter a device brand" />
    ),
  },
  patid: {
    label: 'Patronus ID',
    op: [
      {
        label: ConstraintOperator.EQUALS.label,
        value: ConstraintOperator.EQUALS.value,
      },
      {
        label: ConstraintOperator.NOT_EQUALS.label,
        value: ConstraintOperator.NOT_EQUALS.value,
      },
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: InputProps) => {
      switch (props.constraint.op) {
        case ConstraintOperator.EQUALS.value:
        case ConstraintOperator.NOT_EQUALS.value:
          return <TypeInput {...props} placeholder="Enter a device brand" />;
        default:
          return <AutoSuggestDropDownList {...props} />;
      }
    },
  },
  stage: {
    label: 'Development Stage',
    op: [
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: InputProps) => <MultiDropDownList {...props} />,
  },
  dsf: {
    label: 'Device Screen Factor',
    op: [
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: InputProps) => <MultiDropDownList {...props} />,
  },
  sui: {
    label: 'Service User ID',
    op: [
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: InputProps) => <AutoSuggestDropDownList {...props} />,
  },
  grp: {
    label: 'Group (RESERVED name)',
    op: [
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: InputProps) => <AutoSuggestDropDownList {...props} />,
  },
  ost: {
    label: 'OS Type',
    op: [
      {
        label: ConstraintOperator.EQUALS.label,
        value: ConstraintOperator.EQUALS.value,
      },
      {
        label: ConstraintOperator.NOT_EQUALS.label,
        value: ConstraintOperator.NOT_EQUALS.value,
      },
    ],
    component: (props: InputProps) => (
      <DropDownList
        {...props}
        availableValues={{ ios: 'iOS', android: 'Android' }}
      />
    ),
  },
  fbt: {
    label: 'Food Business Type (food, mart)',
    op: [
      {
        label: ConstraintOperator.EQUALS.label,
        value: ConstraintOperator.EQUALS.value,
      },
      {
        label: ConstraintOperator.NOT_EQUALS.label,
        value: ConstraintOperator.NOT_EQUALS.value,
      },
    ],
    component: (props: InputProps) => <DropDownList {...props} />,
  },
  // fot unsure
  fot: {
    label: 'Food Order Type',
    op: [
      {
        label: ConstraintOperator.EQUALS.label,
        value: ConstraintOperator.EQUALS.value,
      },
      {
        label: ConstraintOperator.NOT_EQUALS.label,
        value: ConstraintOperator.NOT_EQUALS.value,
      },
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: InputProps) => {
      switch (props.constraint.op) {
        case ConstraintOperator.EQUALS.value:
        case ConstraintOperator.NOT_EQUALS.value:
          return <TypeInput {...props} />;
        default:
          return <AutoSuggestDropDownList {...props} />;
      }
    },
  },
  client: {
    label: 'Client Name',
    op: [
      {
        label: ConstraintOperator.EQUALS.label,
        value: ConstraintOperator.EQUALS.value,
      },
      {
        label: ConstraintOperator.NOT_EQUALS.label,
        value: ConstraintOperator.NOT_EQUALS.value,
      },
    ],
    component: (props: InputProps) => {
      return <TypeInput {...props} />;
    },
  },
  id: {
    label: 'Custom ID (iOS)',
    op: [
      {
        label: ConstraintOperator.EQUALS.label,
        value: ConstraintOperator.EQUALS.value,
      },
      {
        label: ConstraintOperator.NOT_EQUALS.label,
        value: ConstraintOperator.NOT_EQUALS.value,
      },
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: InputProps) => {
      switch (props.constraint.op) {
        case ConstraintOperator.IN.value:
        case ConstraintOperator.NOT_IN.value:
          return <AutoSuggestDropDownList {...props} />;
        default:
          return <TypeInput {...props} />;
      }
    },
  },
  build: {
    label: 'Build',
    op: [
      {
        label: ConstraintOperator.EQUALS.label,
        value: ConstraintOperator.EQUALS.value,
      },
      {
        label: ConstraintOperator.NOT_EQUALS.label,
        value: ConstraintOperator.NOT_EQUALS.value,
      },
    ],
    component: (props: InputProps) => {
      return <TypeInput {...props} />;
    },
  },
  ssv: {
    label: 'Scribe SDK version (android, ios)',
    op: [
      {
        label: ConstraintOperator.EQUALS.label,
        value: ConstraintOperator.EQUALS.value,
      },
      {
        label: ConstraintOperator.NOT_EQUALS.label,
        value: ConstraintOperator.NOT_EQUALS.value,
      },
    ],
    component: (props: InputProps) => {
      return <TypeInput {...props} />;
    },
  },
  tmg: {
    label: 'Trident Treatment Group',
    op: [
      {
        label: ConstraintOperator.EQUALS.label,
        value: ConstraintOperator.EQUALS.value,
      },
      {
        label: ConstraintOperator.NOT_EQUALS.label,
        value: ConstraintOperator.NOT_EQUALS.value,
      },
    ],
    component: (props: InputProps) => {
      return <TypeInput {...props} />;
    },
  },
  server: {
    label: 'Server',
    op: [
      {
        label: ConstraintOperator.EQUALS.label,
        value: ConstraintOperator.EQUALS.value,
      },
      {
        label: ConstraintOperator.NOT_EQUALS.label,
        value: ConstraintOperator.NOT_EQUALS.value,
      },
    ],
    component: (props: InputProps) => {
      return <TypeInput {...props} />;
    },
  },
  mcc: {
    label: 'Mobile Country Code',
    op: [
      {
        label: ConstraintOperator.EQUALS.label,
        value: ConstraintOperator.EQUALS.value,
      },
      {
        label: ConstraintOperator.NOT_EQUALS.label,
        value: ConstraintOperator.NOT_EQUALS.value,
      },
    ],
    component: (props: InputProps) => {
      return <TypeInput {...props} />;
    },
  },
  mem: {
    label: 'Mex Merchant',
    op: [
      {
        label: ConstraintOperator.EQUALS.label,
        value: ConstraintOperator.EQUALS.value,
      },
      {
        label: ConstraintOperator.NOT_EQUALS.label,
        value: ConstraintOperator.NOT_EQUALS.value,
      },
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: InputProps) => {
      switch (props.constraint.op) {
        case ConstraintOperator.IN.value:
        case ConstraintOperator.NOT_IN.value:
          return <AutoSuggestDropDownList {...props} />;
        default:
          return <TypeInput {...props} />;
      }
    },
  },
  endpoint: {
    label: 'Endpoint (URL)',
    op: [
      {
        label: ConstraintOperator.EQUALS.label,
        value: ConstraintOperator.EQUALS.value,
      },
      {
        label: ConstraintOperator.NOT_EQUALS.label,
        value: ConstraintOperator.NOT_EQUALS.value,
      },
    ],
    component: (props: InputProps) => {
      return <TypeInput {...props} />;
    },
  },
  ec2: {
    label: 'EC2 Instance ID',
    op: [
      {
        label: ConstraintOperator.EQUALS.label,
        value: ConstraintOperator.EQUALS.value,
      },
      {
        label: ConstraintOperator.NOT_EQUALS.label,
        value: ConstraintOperator.NOT_EQUALS.value,
      },
    ],
    component: (props: InputProps) => {
      return <TypeInput {...props} />;
    },
  },
  dom: {
    label: 'Domain',
    op: [
      {
        label: ConstraintOperator.EQUALS.label,
        value: ConstraintOperator.EQUALS.value,
      },
      {
        label: ConstraintOperator.NOT_EQUALS.label,
        value: ConstraintOperator.NOT_EQUALS.value,
      },
    ],
    component: (props: InputProps) => {
      return <TypeInput {...props} />;
    },
  },
  service: {
    label: 'Destination Service',
    op: [
      {
        label: ConstraintOperator.EQUALS.label,
        value: ConstraintOperator.EQUALS.value,
      },
      {
        label: ConstraintOperator.NOT_EQUALS.label,
        value: ConstraintOperator.NOT_EQUALS.value,
      },
    ],
    component: (props: InputProps) => {
      return <TypeInput {...props} />;
    },
  },
  dol: {
    label: 'Dropoff Location',
    op: [
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
  },
  mexl: {
    label: 'Merchant Location',
    op: [
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
  },
  pul: {
    label: 'Pickup Location',
    op: [
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
  },
  pulgh: {
    label: 'Pickup Location Geohash',
    op: [
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: InputProps) => <AutoSuggestDropDownList {...props} />,
  },
  daxl: {
    label: 'Driver Location',
    op: [
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
  },
  vhp: {
    label: 'Vehicle Plate',
    op: [
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: InputProps) => <AutoSuggestDropDownList {...props} />,
  },
  pid: {
    label: 'Public User ID',
    op: [
      {
        label: ConstraintOperator.IN.label,
        value: ConstraintOperator.IN.value,
      },
      {
        label: ConstraintOperator.NOT_IN.label,
        value: ConstraintOperator.NOT_IN.value,
      },
    ],
    component: (props: InputProps) => <AutoSuggestDropDownList {...props} />,
  },
  dvm: {
    label: 'Device Model',
    op: [
      {
        label: ConstraintOperator.EQUALS.label,
        value: ConstraintOperator.EQUALS.value,
      },
    ],
    component: (props: InputProps) => <TypeInput {...props} />,
  },
};

export default facetMap;
